import {
  GridColDef,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridSortModel,
  GridState,
} from '@mui/x-data-grid-pro'
import { UseData } from './useData'
import { useAppDispatch } from '@user/store/hooks'
import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferences,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { useCallback } from 'react'
import { FieldState, getOrderByFunc } from '@user/components/xGrid'
import { getSaveListViewPreferenceFunc } from '@user/store/userPreferences'
import { defaultOrderBy } from '@user/store/import/typings'
import { useSelector } from 'react-redux'
import {
  selectImportFilters,
  setImportsPage,
  setImportsPerPage,
} from '@user/store/import/slice'
import { useSaveImportFilters } from '@user/store/import/hooks/useSaveImportFilters'
import { getImports } from '@user/store/import/api'
import { useBrowserHistoryData } from '@user/utils/useBrowserHistoryData'

type Props = Pick<UseData, 'densityMode'> & {
  columns: GridColDef[]
}

export function useHandlers(props: Props) {
  const { densityMode } = props
  const dispatch = useAppDispatch()
  const saveListViewPreference = getSaveListViewPreferenceFunc('import')
  const { filterSet } = useSelector(selectImportFilters)
  const saveFilters = useSaveImportFilters()

  const onDensityChange = useCallback(
    (density: string) => {
      if (densityMode != density) {
        dispatch(
          saveGeneralPreferences({
            type: IUserPreferenceGeneralListItemType.ImportDensityMode,
            value: density,
          }),
        )
      }
    },
    [densityMode, dispatch],
  )

  const onSaveColumnState = useCallback(
    (columnOrder: FieldState[], columns: GridColDef[]) => {
      const mappedData = columnOrder?.map((row: FieldState) => {
        const found = columns?.find(
          (findRow: GridColDef) => findRow?.field == row?.field,
        )
        if (found && found?.width) {
          return {
            ...row,
            width: found?.width?.toString(),
          }
        }
        return {
          ...row,
        }
      })
      saveListViewPreference(dispatch, mappedData)
    },
    [dispatch, saveListViewPreference],
  )

  const onStateChange = useCallback(
    (state: GridState) => {
      if (state.density) {
        onDensityChange(state.density.value)
      }
    },
    [onDensityChange],
  )

  const getOrderBy = getOrderByFunc(defaultOrderBy)

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel, details) => {
      let newOrderBy: string
      if (sortModel.length === 0) {
        const currentSortModel = details.api.getSortModel()[0]
        newOrderBy = `${currentSortModel.field} ${
          currentSortModel.sort === 'asc' ? 'desc' : 'asc'
        }`
      } else {
        newOrderBy = getOrderBy(sortModel)
      }

      const { orderBy } = filterSet

      if (newOrderBy !== orderBy)
        saveFilters({ ...filterSet, orderBy: newOrderBy })
      dispatch(getImports())
    },
    [dispatch, filterSet, getOrderBy, saveFilters],
  )

  const { setBrowserHistory } = useBrowserHistoryData()

  const onPaginationModelChange = useCallback(
    (pageModel: GridPaginationModel) => {
      const { page, pageSize } = pageModel
      sessionStorage.setItem('pageNumber', (page + 1)?.toString())
      sessionStorage.setItem('pageSize', pageSize?.toString())
      setBrowserHistory()
      dispatch(setImportsPerPage(pageSize))
      dispatch(setImportsPage(page + 1))
      dispatch(getImports())
    },
    [dispatch, setBrowserHistory],
  )

  const onColumnWidthChange = useCallback(
    (
      columnData: GridColDef,
      columns: GridColDef[],
      columnVisibility: GridColumnVisibilityModel,
    ) => {
      const mapToFieldState: FieldState[] = [...columns]?.map(
        (row: GridColDef) => {
          if (row?.width) {
            return {
              field: row?.field,
              hide: !columnVisibility?.[row?.field],
              width: row?.width?.toString(),
            }
          }
          return {
            field: row?.field,
            hide: !columnVisibility?.[row?.field],
          }
        },
      )

      const foundColumn = mapToFieldState?.find(
        (row: FieldState) => row?.field == columnData?.field,
      )
      if (foundColumn) {
        const indexOf = mapToFieldState?.findIndex(
          (row) => row?.field == columnData?.field,
        )
        const newColumnObject = {
          ...foundColumn,
          width: columnData?.width?.toString(),
        }
        mapToFieldState.splice(indexOf, 1, newColumnObject)
        saveListViewPreference(dispatch, mapToFieldState)
      }
    },
    [dispatch, saveListViewPreference],
  )

  return {
    onDensityChange,
    onStateChange,
    onSaveColumnState,
    onSortModelChange,
    onPaginationModelChange,
    onColumnWidthChange,
  }
}
